<template>
  <v-form class="step-basic" :disabled="readOnly">
    <v-row class="step-basic-row" align="stretch">
      <v-col cols="12" xl="7" class="col-form d-flex flex-column ga-4">
        <section class="info-card">
          <div class="panel-title">
            <span class="panel-accent"></span>
            <span class="panel-title-text">项目信息</span>
          </div>
          <p class="field-hint">
            带 * 为必填。电站地址可与右侧地图选点配合填写；经纬度在「电站位置地图」中维护。
          </p>
          <v-row dense>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label">项目名称</span>
                <v-text-field v-model="form.projectName" placeholder="请输入项目名称" variant="outlined" density="compact"
                  hide-details="auto" rounded="xl" class="form-line__control" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label form-line__label--required"><span
                    class="required-mark">*</span><span>建站日期</span></span>
                <v-menu v-model="commissioningDateMenuVisible" :close-on-content-click="false" location="bottom"
                  offset="8">
                  <template #activator="{ props: menuProps }">
                    <v-text-field v-bind="menuProps" :model-value="formatCommissioningDate(form.commossioningTime)"
                      placeholder="请选择建站日期" variant="outlined" density="compact" hide-details="auto" rounded="xl"
                      readonly class="form-line__control" append-inner-icon="mdi-calendar-month-outline" />
                  </template>
                  <v-date-picker :model-value="commissioningDateValue" color="primary"
                    @update:model-value="handleCommissioningDateChange" />
                </v-menu>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label form-line__label--required"><span
                    class="required-mark">*</span><span>项目编码</span></span>
                <v-text-field v-model="form.projectCode" placeholder="请输入项目编码" variant="outlined" density="compact"
                  hide-details="auto" rounded="xl" class="form-line__control" :disabled="hasStationId" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label">项目简称</span>
                <v-text-field v-model="form.projectShortName" placeholder="请输入项目简称" variant="outlined" density="compact"
                  hide-details="auto" rounded="xl" class="form-line__control" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label">装机功率</span>
                <v-text-field v-model="form.maxpower" type="number" step="0.01" placeholder="请输入装机功率" variant="outlined"
                  density="compact" hide-details="auto" rounded="xl" class="form-line__control">
                  <template #append-inner>
                    <span class="text-orange-darken-1">kW</span>
                  </template>
                </v-text-field>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label">装机容量</span>
                <v-text-field v-model="form.capacity" type="number" step="0.01" placeholder="请输入装机容量" variant="outlined"
                  density="compact" hide-details="auto" rounded="xl" class="form-line__control">
                  <template #append-inner>
                    <span class="text-orange-darken-1">kWh</span>
                  </template>
                </v-text-field>
              </div>
            </v-col>
          </v-row>

          <div class="panel-title panel-title--subsection">
            <span class="panel-accent"></span>
            <span class="panel-title-text">电站地址</span>
          </div>
          <p class="field-hint">
            按国家 / 省 / 市 / 区县填写行政区。
          </p>
          <v-row dense class="address-grid">
            <v-col cols="12" md="6">
              <div class="form-line form-line--compact form-line--address">
                <span class="form-line__label form-line__label--required"><span
                    class="required-mark">*</span><span>地区</span></span>
                <v-select v-model="form.area" :items="areaOptions" item-title="title" item-value="value"
                  variant="outlined" density="compact" hide-details="auto" rounded="xl" placeholder="请选择大区"
                  class="form-line__control" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line form-line--compact form-line--address">
                <span class="form-line__label form-line__label--required"><span
                    class="required-mark">*</span><span>省</span></span>
                <v-combobox v-model="form.province" :items="provinceOptions" variant="outlined" density="compact"
                  hide-details="auto" rounded="xl" clearable placeholder="请输入或选择" class="form-line__control"
                  hide-no-data @update:model-value="onProvinceChange" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line form-line--compact form-line--address">
                <span class="form-line__label">市</span>
                <v-combobox v-model="form.city" :items="cityOptions" variant="outlined" density="compact"
                  hide-details="auto" rounded="xl" clearable placeholder="请输入或选择" :disabled="readOnly || !form.province"
                  class="form-line__control" hide-no-data @update:model-value="onCityChange" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line form-line--compact form-line--address">
                <span class="form-line__label">区 / 县</span>
                <v-combobox v-model="form.district" :items="districtOptions" variant="outlined" density="compact"
                  hide-details="auto" rounded="xl" clearable placeholder="请输入或选择" :disabled="readOnly || !form.city"
                  class="form-line__control" hide-no-data />
              </div>
            </v-col>
            <v-col cols="12">
              <div class="form-line form-line--compact form-line--address">
                <span class="form-line__label">电站地址</span>
                <v-text-field v-model="form.address" placeholder="请输入电站地址" variant="outlined" density="compact"
                  hide-details="auto" rounded="xl" class="form-line__control" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label form-line__label--required"><span class="required-mark">*</span><span>投资方
                    (EPC)</span></span>
                <v-autocomplete v-model="form.investorEpc" v-model:search="investorEpcKeyword"
                  :items="investorEpcOptions" item-title="title" item-value="value" no-filter
                  :list-props="{ lines: 'two', density: 'compact' }" variant="outlined" density="compact"
                  hide-details="auto" rounded="xl" class="form-line__control" placeholder="请输入投资方后选择" hide-no-data
                  clearable :loading="investorEpcLoading">
                  <template v-if="!readOnly" #append-inner>
                    <v-btn icon size="small" variant="text" color="primary" aria-label="新增投资方" v-tooltip:top="'新增客户'"
                      @click="openInvestorAdd">
                      <v-icon size="20">mdi-plus</v-icon>
                    </v-btn>
                  </template>
                </v-autocomplete>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label form-line__label--required"><span
                    class="required-mark">*</span><span>业主方</span></span>
                <v-autocomplete v-model="form.ownerEnterprise" v-model:search="ownerEnterpriseKeyword"
                  :items="proprietorSelectOptions" item-title="title" item-value="value" no-filter
                  :list-props="{ lines: 'two', density: 'compact' }" variant="outlined" density="compact"
                  hide-details="auto" rounded="xl" class="form-line__control" placeholder="请输入业主方后选择" hide-no-data
                  clearable :loading="ownerEnterpriseLoading">
                  <template v-if="!readOnly" #append-inner>
                    <v-btn icon size="small" variant="text" color="primary" aria-label="新增业主方" v-tooltip:top="'新增客户'"
                      @click="openOwnerEnterpriseAdd">
                      <v-icon size="20">mdi-plus</v-icon>
                    </v-btn>
                  </template>
                </v-autocomplete>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label">渠道方</span>
                <v-autocomplete v-model="form.channelFramework" v-model:search="channelFrameworkKeyword"
                  :items="channelSelectOptions" item-title="title" item-value="value" no-filter
                  :list-props="{ lines: 'two', density: 'compact' }" variant="outlined" density="compact"
                  hide-details="auto" rounded="xl" clearable class="form-line__control" placeholder="请输入渠道方后选择"
                  hide-no-data :loading="channelFrameworkLoading">
                  <template v-if="!readOnly" #append-inner>
                    <v-btn icon size="small" variant="text" color="primary" aria-label="新增渠道方" v-tooltip:top="'新增客户'"
                      @click="openChannelAdd">
                      <v-icon size="20">mdi-plus</v-icon>
                    </v-btn>
                  </template>
                </v-autocomplete>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label">业务模式</span>
                <v-select v-model="form.businessMode" :items="businessModeOptions" item-title="title" item-value="value"
                  variant="outlined" density="compact" hide-details="auto" rounded="xl" class="form-line__control" />
              </div>
            </v-col>
          </v-row>
        </section>

        <section class="info-card">
          <div class="panel-title">
            <span class="panel-accent"></span>
            <span class="panel-title-text">运营信息</span>
          </div>
          <p class="field-hint">
            用于登记所属行业、运行策略，以及场地是否配套光伏、充电桩、变压器相关设施条件。
          </p>
          <v-row dense>
            <v-col cols="12">
              <div class="form-line">
                <span class="form-line__label">策略控制</span>
                <div class="form-line__control form-line__control--switch">
                  <el-switch v-model="form.strategySwitch" :disabled="readOnly" class="strategy-switch" />
                </div>
              </div>
            </v-col>
            <v-col cols="12">
              <div class="form-line">
                <span class="form-line__label">行业类别</span>
                <v-select v-model="form.industryCategory" :items="industryCategoryOptions" item-title="title"
                  item-value="value" :list-props="{ density: 'compact' }" placeholder="请选择" variant="outlined"
                  density="compact" hide-details="auto" rounded="xl" class="form-line__control" />
              </div>
            </v-col>
            <v-col cols="12">
              <div class="form-line form-line--device-flags">
                <span class="form-line__label">设备配置</span>
                <v-text-field v-if="readOnly" :model-value="deviceFlagsReadonlyText" readonly variant="outlined"
                  density="compact" hide-details="auto" rounded="xl" class="form-line__control" placeholder="—" />
                <v-select v-else v-model="form.deviceFlags" :items="deviceFlagOptions" item-title="title"
                  item-value="value" :list-props="{ density: 'compact' }" multiple chips closable-chips
                  placeholder="请选择电站配备的设备类型" variant="outlined" density="compact" hide-details="auto" rounded="xl"
                  class="form-line__control" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label">运营模式</span>
                <v-select v-model="form.operationModel" :items="operationModelOptions" item-title="title"
                  item-value="value" placeholder="请选择" variant="outlined" density="compact" hide-details="auto"
                  rounded="xl" class="form-line__control" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label">用电类别</span>
                <v-select v-model="form.electricityCategory" :items="electricityCategoryOptions" item-title="title"
                  item-value="value" placeholder="请选择" variant="outlined" density="compact" hide-details="auto"
                  rounded="xl" class="form-line__control" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label">电压等级</span>
                <v-select v-model="form.voltage" :items="voltageLevelOptions" item-title="title" item-value="value"
                  placeholder="请选择" variant="outlined" density="compact" hide-details="auto" rounded="xl"
                  class="form-line__control">
                  <template #append-inner>
                    <span class="text-orange-darken-1">kV</span>
                  </template>
                </v-select>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label">电站类型</span>
                <v-select v-model="form.stationType" :items="stationTypeOptions" item-title="title" item-value="value"
                  placeholder="请选择" variant="outlined" density="compact" hide-details="auto" rounded="xl"
                  class="form-line__control" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label">应用场景</span>
                <v-select v-model="form.applicationScenario" :items="applicationScenarioOptions" item-title="title"
                  item-value="value" placeholder="请选择" variant="outlined" density="compact" hide-details="auto"
                  rounded="xl" class="form-line__control" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label">计费模式</span>
                <v-select v-model="form.billingMode" :items="billingModeOptions" item-title="title" item-value="value"
                  placeholder="请选择" variant="outlined" density="compact" hide-details="auto" rounded="xl"
                  class="form-line__control" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label form-line__label--required"><span
                    class="required-mark">*</span><span>电表模式</span></span>
                <v-select :model-value="form.meterModel" :items="meterModelOptions" item-title="title"
                  item-value="value" placeholder="请选择" variant="outlined" density="compact" hide-details="auto"
                  rounded="xl" class="form-line__control" @update:model-value="handleMeterModelChange" />
              </div>
            </v-col>
            <v-col cols="12">
              <div class="form-line form-line--control-perms">
                <span class="form-line__label">自定义控制权限 <v-icon size="16" color="primary"
                    v-tooltip:top="'设置电站的特定控制权限'">mdi-information-outline</v-icon></span>
                <div class="form-line__control control-perms">
                  <el-checkbox v-model="form.antiBackflow" :disabled="readOnly" label="防逆流" />
                  <el-checkbox v-model="form.transformerCapacityRatio" :disabled="readOnly" label="箱变容量变比" />
                  <el-checkbox v-model="form.reactivePowerWorking" :disabled="readOnly" label="无功工作模式" />
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label" :class="{ 'form-line__label--required': hasCustomControlPermissions }">
                  <span v-if="hasCustomControlPermissions" class="required-mark">*</span><span>联系手机号</span>
                </span>
                <v-text-field v-model="form.contactPhone" maxlength="11" placeholder="请输入联系手机号" variant="outlined"
                  density="compact" hide-details="auto" rounded="xl" class="form-line__control" />
              </div>
            </v-col>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label">自动需量 <v-icon size="16" color="primary"
                    v-tooltip:top="'开启时系统自动控制需量'">mdi-information-outline</v-icon></span>
                <div class="form-line__control form-line__control--switch">
                  <el-switch v-model="form.autoDemand" :disabled="readOnly" class="strategy-switch" />
                </div>
              </div>
            </v-col>
          </v-row>
        </section>

        <section class="info-card">
          <div class="panel-title">
            <span class="panel-accent"></span>
            <span class="panel-title-text">其他信息</span>
          </div>
          <v-row dense>
            <v-col cols="12" md="6">
              <div class="form-line">
                <span class="form-line__label">显示优先级 <v-icon size="16" color="primary"
                    v-tooltip:top="'电站列表中电站的显示顺序，数字越大，优先级越高，显示越靠前'">mdi-information-outline</v-icon> </span>
                <v-text-field v-model="form.sort" type="number" step="1" placeholder="数字越大越靠前展示" variant="outlined"
                  density="compact" hide-details="auto" rounded="xl" class="form-line__control" />
              </div>
            </v-col>
          </v-row>
        </section>
      </v-col>

      <v-col cols="12" xl="5" class="col-side d-flex flex-column ga-4">
        <div class="form-section-wrap">
          <section class="info-card info-card--map">
            <div class="panel-title">
              <span class="panel-accent"></span>
              <span class="panel-title-text">电站位置地图</span>
            </div>
            <p class="coord-line">
              当前坐标（{{ coordDisplay.longitude }}, {{ coordDisplay.latitude }}）
            </p>
            <p class="field-hint">
              在地图上点击可选定位置并写入经纬度；支持在下方手动微调坐标。
            </p>
            <div class="form-line mb-3 map-search-line map-search-line--full">
              <span class="form-line__label" style="min-width: auto;">搜索地址</span>
              <div class="form-line__control map-search-wrap">
                <v-text-field v-model="mapSearchKeyword" placeholder="请输入关键字" variant="outlined" density="compact"
                  hide-details rounded="xl" append-inner-icon="mdi-magnify" class="form-line__control map-search-input"
                  @update:model-value="handleSearch" @click:append-inner="handleSearch" @keyup.enter="handleSearch" />
                <div v-if="!readOnly && searchResults.length > 0" class="map-search-dropdown">
                  <div v-for="item in searchResults" :key="item.id" class="map-search-dropdown__item"
                    @click="selectAddress(item)">
                    <div class="map-search-dropdown__title">{{ item.name }}</div>
                    <div class="map-search-dropdown__address">{{ item.address }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div ref="mapContainerRef" class="map-canvas" :class="{ 'map-canvas--readonly': readOnly }" />
            <div class="mt-3 coord-row">
              <div class="form-line coord-line-form">
                <span class="form-line__label" style="min-width: auto;">经度</span>
                <v-text-field v-model.number="form.longitude" type="number" step="0.000001" variant="outlined"
                  density="compact" hide-details="auto" rounded="xl" class="form-line__control coord-input" />
              </div>
              <div class="form-line coord-line-form">
                <span class="form-line__label" style="min-width: auto;">纬度</span>
                <v-text-field v-model.number="form.latitude" type="number" step="0.000001" variant="outlined"
                  density="compact" hide-details="auto" rounded="xl" class="form-line__control coord-input" />
              </div>
            </div>
          </section>

          <section class="info-card mt-4">
            <div class="panel-title">
              <span class="panel-accent"></span>
              <span class="panel-title-text">电站照片</span>
            </div>
            <p class="field-hint">请上传电站照片，大小不超过 5 MB</p>
            <input :ref="bindHiddenFileInput" type="file" accept="image/*" class="d-none" :disabled="readOnly"
              @change="onHiddenFileChange" />
            <div class="photo-slots d-flex ga-3">
              <div class="photo-slot rounded-lg d-flex align-center justify-center"
                :class="{ 'photo-slot--readonly': readOnly }" :role="readOnly ? undefined : 'button'"
                :tabindex="readOnly ? -1 : 0" @click="!readOnly && triggerPick()"
                @keydown.enter.prevent="!readOnly && triggerPick()">
                <v-img v-if="photoSlot?.preview" :src="photoSlot.preview" cover class="photo-preview" />
                <button v-if="photoSlot?.preview && !readOnly" type="button" class="photo-slot__remove"
                  @click.stop="removePhotoSlot()">
                  <v-icon size="16" color="white">mdi-close</v-icon>
                </button>
                <v-icon v-else size="32" color="primary">mdi-plus</v-icon>
              </div>
            </div>
          </section>
        </div>
      </v-col>
    </v-row>

    <CustomerAddDialog v-model="addDialogVisible" :customer-type="addDialogType" @created="onCustomerCreated" />
  </v-form>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue'
import dayjs from 'dayjs'
import 'qiankun-map-2d.css'
import { Map } from 'qiankun-map-2d'
import { eui } from 'ems-ui'
import { useRoute } from 'vue-router'
import { useStationAddBasic } from '../useStationAddBasic'
import CustomerAddDialog from './CustomerAddDialog.vue'
import stationPinIcon from '@/assets/font_icon.png'

const props = defineProps({
  readOnly: { type: Boolean, default: false },
})

const route = useRoute()

const {
  form,
  mapSearchKeyword,
  provinceOptions,
  cityOptions,
  districtOptions,
  onProvinceChange,
  onCityChange,
  syncRegionCascadeFromForm,
  operationModelOptions,
  industryCategoryOptions,
  deviceFlagOptions,
  electricityCategoryOptions,
  businessModeOptions,
  areaOptions,
  investorEpcOptions,
  proprietorSelectOptions,
  channelSelectOptions,
  investorEpcKeyword,
  ownerEnterpriseKeyword,
  channelFrameworkKeyword,
  investorEpcLoading,
  ownerEnterpriseLoading,
  channelFrameworkLoading,
  voltageLevelOptions,
  meterModelOptions,
  stationTypeOptions,
  applicationScenarioOptions,
  billingModeOptions,
  photoSlot,
  hiddenFileInput,
  triggerPick,
  onHiddenFileChange,
  removePhotoSlot,
  refreshCustomerOptionsByType,
  applyCreatedCustomer,
} = useStationAddBasic()

/** 只读态「设备配置」以纯文本展示，避免 chips 在窄容器里挤压重叠、与其它字段视觉不一致 */
const deviceFlagsReadonlyText = computed(() => {
  const flags = Array.isArray(form.deviceFlags) ? form.deviceFlags : []
  if (!flags.length) return ''
  return flags
    .map((v) => deviceFlagOptions.find((opt) => opt.value === v)?.title)
    .filter(Boolean)
    .join('、')
})

/** 勾选任一自定义控制权限时，联系手机号变为必填（与 emind-ui-next 校验规则一致） */
const hasCustomControlPermissions = computed(() => {
  return !!form.antiBackflow || !!form.transformerCapacityRatio || !!form.reactivePowerWorking
})

const bindHiddenFileInput = (el) => {  hiddenFileInput.value = el
}

// 新增客户弹框：按触发的选择器锁定客户类型，成功后刷新对应下拉并自动选中
const addDialogVisible = ref(false)
const addDialogType = ref('')

function openInvestorAdd() {
  addDialogType.value = '投资方'
  addDialogVisible.value = true
}

function openOwnerEnterpriseAdd() {
  addDialogType.value = '业主方'
  addDialogVisible.value = true
}

function openChannelAdd() {
  addDialogType.value = '渠道方'
  addDialogVisible.value = true
}

async function onCustomerCreated({ customerType, name }) {
  try {
    await refreshCustomerOptionsByType(customerType)
  } catch {
    /* 刷新失败时仍回填新客户，避免选不中 */
  }
  applyCreatedCustomer(customerType, name)
}

const mapContainerRef = ref(null)
const commissioningDateMenuVisible = ref(false)
const searchResults = ref([])
const commissioningDateValue = computed(() => form.commossioningTime || null)
const coordDisplay = computed(() => ({
  longitude: Number.isFinite(form.longitude) ? form.longitude.toFixed(6) : '--',
  latitude: Number.isFinite(form.latitude) ? form.latitude.toFixed(6) : '--',
}))
const hasStationId = computed(() => route.query.stationId != null && route.query.stationId !== '')
let mapInstance = null
let dmlLayer = null
const MARKER_ID = 'station-picker'
let suppressMarkerWatch = false
let geoInitToken = 0

function handleMeterModelChange(value) {
  const nextValue = value === '' || value == null ? value : Number(value)
  const currentValue = form.meterModel === '' || form.meterModel == null ? form.meterModel : Number(form.meterModel)

  if (hasStationId.value && currentValue === 1 && nextValue === 0) {
    eui.showToast('实体表不能修改为虚拟表', 'error')
    return
  }

  form.meterModel = nextValue
}

function toLonLat([x, y]) {
  const lon = (x / 20037508.34) * 180
  let lat = (y / 20037508.34) * 180
  lat = (180 / Math.PI) * (2 * Math.atan(Math.exp((lat * Math.PI) / 180)) - Math.PI / 2)
  return [lon, lat]
}

function placeMarker(lon, lat) {
  if (!dmlLayer) return
  // 单选场景：先清空整层，避免重复选点叠加
  try {
    dmlLayer.clear?.()
  } catch {
    /* 忽略首次清空异常 */
  }
  dmlLayer.addMarker({
    id: MARKER_ID,
    lon,
    lat,
    name: 'station-picker',
    icon: { url: stationPinIcon, width: 32, height: 32, anchor: [0.5, 1] },
    anchor: [0.5, 1],
    scale: 1,
    opacity: 1,
  })
}

/** 提取搜索结果中的 [lon, lat]，兼容 POI / 行政区两种返回 */
function parseLonLatFromSearchResult(res) {
  if (!res || typeof res !== 'object') return null
  const pickStr = (s) => {
    if (typeof s !== 'string') return null
    const [lonStr, latStr] = s.split(',')
    const lon = Number(lonStr)
    const lat = Number(latStr)
    return Number.isFinite(lon) && Number.isFinite(lat) ? [lon, lat] : null
  }
  if (Array.isArray(res.pois) && res.pois.length) {
    const hit = pickStr(res.pois[0]?.lonlat)
    if (hit) return hit
  }
  if (res.area) {
    const hit = pickStr(res.area?.lonlat)
    if (hit) return hit
  }
  return null
}

function normalizePoi(item, index = 0) {
  let lonlat = []
  if (item?.lonlat) {
    if (typeof item.lonlat === 'string') {
      lonlat = item.lonlat.split(',').map((val) => parseFloat(String(val).trim())).filter((val) => !Number.isNaN(val))
    } else if (Array.isArray(item.lonlat)) {
      lonlat = item.lonlat.map((val) => (typeof val === 'string' ? parseFloat(val.trim()) : Number(val)))
        .filter((val) => Number.isFinite(val))
    }
  } else if (item?.longitude && item?.latitude) {
    lonlat = [parseFloat(item.longitude), parseFloat(item.latitude)]
  } else if (item?.lng && item?.lat) {
    lonlat = [parseFloat(item.lng), parseFloat(item.lat)]
  }

  return {
    id: item?.hotPointID || item?.id || `search-${index}`,
    name: item?.name || '',
    address: item?.address || item?.name || '',
    fullAddress: item?.fullAddress || item?.address || item?.name || '',
    lonlat,
    province: item?.province || item?.provinceName || '',
    city: item?.city || item?.cityName || '',
    district: item?.district || item?.districtName || '',
    adcode: item?.adcode || item?.adCode || '',
  }
}

async function handleSearch() {
  const keyWord = (mapSearchKeyword.value || '').trim()
  if (!keyWord) {
    searchResults.value = []
    return
  }
  if (!mapInstance?.AC?.commonSearch) {
    eui.showToast?.('地图服务未就绪，请稍后再试', 'error')
    return
  }
  try {
    const result = await mapInstance.AC.commonSearch({ keyWord })
    searchResults.value = Array.isArray(result?.pois)
      ? result.pois.map((item, index) => normalizePoi(item, index)).filter((item) => item.lonlat.length >= 2)
      : []

    if (!searchResults.value.length) {
      const area = parseLonLatFromSearchResult(result)
      if (area) {
        searchResults.value = [{
          id: 'search-area',
          name: keyWord,
          address: keyWord,
          fullAddress: keyWord,
          lonlat: area,
          province: '',
          city: '',
          district: '',
          adcode: '',
        }]
      }
    }
  } catch (err) {
    searchResults.value = []
  }
}

function fillAddressFromSelection(item) {
  const nextProvince = item?.province?.trim?.() || ''
  const nextCity = item?.city?.trim?.() || ''
  const nextDistrict = item?.district?.trim?.() || ''

  if (nextProvince) form.province = nextProvince
  if (nextCity) form.city = nextCity
  if (nextDistrict) form.district = nextDistrict

  syncRegionCascadeFromForm()
  form.address = item?.address || item?.name || ''
}

function selectAddress(item) {
  if (!Array.isArray(item?.lonlat) || item.lonlat.length < 2) return
  const [lon, lat] = item.lonlat
  suppressMarkerWatch = true
  form.longitude = Number(lon.toFixed(6))
  form.latitude = Number(lat.toFixed(6))
  placeMarker(lon, lat)
  setTimeout(() => mapInstance?.VC?.flyToLonlat?.([lon, lat]), 0)
  mapSearchKeyword.value = item?.fullAddress || item?.address || item?.name || ''
  fillAddressFromSelection(item)
  searchResults.value = []
  Promise.resolve().then(() => {
    suppressMarkerWatch = false
  })
}

function formatCommissioningDate(value) {
  if (!value) return ''
  const d = dayjs(value)
  return d.isValid() ? d.format('YYYY年MM月DD日') : String(value)
}

function handleCommissioningDateChange(value) {
  const pickedValue = Array.isArray(value) ? value[0] : value
  if (!pickedValue) {
    form.commossioningTime = ''
    commissioningDateMenuVisible.value = false
    return
  }
  const d = dayjs(pickedValue)
  form.commossioningTime = d.isValid() ? d.format('YYYY-MM-DD') : ''
  commissioningDateMenuVisible.value = false
}

async function fillAddressFromMapPoint(lon, lat) {
  if (!mapInstance?.AC?.getAddressByLonLat) return
  try {
    const result = await mapInstance.AC.getAddressByLonLat({ lon, lat })
    const area = result?.area || result?.data?.area || {}
    const poi = result?.poi || result?.data?.poi || {}
    fillAddressFromSelection({
      province: area?.province || area?.provinceName || '',
      city: area?.city || area?.cityName || '',
      district: area?.district || area?.districtName || '',
      address: poi?.address || poi?.name || area?.name || '',
      name: poi?.name || area?.name || '',
    })
  } catch {
    /* 忽略逆地理解析失败 */
  }
}

function onMapClick(e) {
  if (props.readOnly) return
  if (!e?.coordinate) return
  const [lon, lat] = toLonLat(e.coordinate)
  suppressMarkerWatch = true
  form.longitude = Number(lon.toFixed(6))
  form.latitude = Number(lat.toFixed(6))
  placeMarker(lon, lat)
  void fillAddressFromMapPoint(lon, lat)
  // watch 在下一个 tick 触发，置回 false
  Promise.resolve().then(() => {
    suppressMarkerWatch = false
  })
}

function setMapCenter(lon, lat, zoom = 16) {
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return
  placeMarker(lon, lat)
  mapInstance?.VC?.flyToLonlat?.([lon, lat])
  if (typeof mapInstance?.setZoom === 'function') {
    mapInstance.setZoom(zoom)
  } else if (typeof mapInstance?.VC?.setZoom === 'function') {
    mapInstance.VC.setZoom(zoom)
  }
}

function applyInitialCoordinate(lon, lat) {
  suppressMarkerWatch = true
  form.longitude = Number(lon.toFixed(6))
  form.latitude = Number(lat.toFixed(6))
  setMapCenter(lon, lat)
  void fillAddressFromMapPoint(lon, lat)
  Promise.resolve().then(() => {
    suppressMarkerWatch = false
  })
}

function requestCurrentGeolocation() {
  if (props.readOnly || hasStationId.value) return
  if (!navigator.geolocation) return

  const currentToken = ++geoInitToken
  navigator.geolocation.getCurrentPosition(
    (position) => {
      if (currentToken !== geoInitToken) return
      const lon = Number(position?.coords?.longitude)
      const lat = Number(position?.coords?.latitude)
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) return
      if (Number.isFinite(form.longitude) || Number.isFinite(form.latitude)) return
      applyInitialCoordinate(lon, lat)
    },
    () => {
      /* 忽略定位失败，保留地图默认中心 */
    },
    {
      enableHighAccuracy: true,
      timeout: 8000,
      maximumAge: 300000,
    },
  )
}

onMounted(() => {
  if (!mapContainerRef.value) return
  const hasCoord = Number.isFinite(form.longitude) && Number.isFinite(form.latitude)
  const center = hasCoord ? [form.longitude, form.latitude] : [108.81522, 34.17146]
  mapInstance = new Map({
    mapContainer: mapContainerRef.value,
    center,
    zoom: hasCoord ? 16 : 5,
  })
  dmlLayer = mapInstance?.LC?.dmlLayer ?? null
  if (dmlLayer) {
    if (hasCoord) placeMarker(center[0], center[1])
  } else {
    const retry = setInterval(() => {
      dmlLayer = mapInstance?.LC?.dmlLayer ?? null
      if (dmlLayer) {
        clearInterval(retry)
        if (Number.isFinite(form.longitude) && Number.isFinite(form.latitude)) {
          placeMarker(form.longitude, form.latitude)
        }
      }
    }, 80)
    setTimeout(() => clearInterval(retry), 3000)
  }
  mapInstance?.addEventListener?.('click', onMapClick)
  requestCurrentGeolocation()
})

onBeforeUnmount(() => {
  try {
    mapInstance?.removeEventListener?.('click', onMapClick)
  } catch {
    /* 忽略卸载阶段异常 */
  }
  try {
    mapInstance?.LC?.destory?.()
  } catch {
    /* 忽略卸载阶段异常 */
  }
  mapInstance = null
  dmlLayer = null
})

watch(
  () => mapSearchKeyword.value,
  (value) => {
    if (!String(value || '').trim()) searchResults.value = []
  },
)

watch(
  () => [form.longitude, form.latitude],
  ([lon, lat]) => {
    if (suppressMarkerWatch) return
    if (Number.isFinite(lon) && Number.isFinite(lat)) {
      placeMarker(lon, lat)
      mapInstance?.VC?.flyToLonlat?.([lon, lat])
    }
  },
)
</script>

<style scoped lang="scss">
.step-basic {
  min-width: 0;
  max-width: 100%;
}

.step-basic-row {
  margin-top: 0;
  max-width: 100%;
}

.info-card {
  position: relative;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.55);
  border: 0.5px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 5px rgba(169, 169, 169, 0.15);
  backdrop-filter: blur(10px);
  padding: 20px 22px;
}

.panel-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.panel-title--subsection {
  margin-top: 18px;
}

.panel-accent {
  width: 4px;
  height: 18px;
  border-radius: 2px;
  background: #3A68FF;
}

.panel-title-text {
  color: #2E2E2E;
  font-family: "HarmonyOS Sans SC", "PingFang SC", sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 28px;
}

.address-grid {
  row-gap: 4px;
}

.field-hint {
  color: rgba(46, 46, 46, 0.55);
  font-size: 12px;
  line-height: 1.55;
  margin: 0 0 12px;
  max-width: 48rem;
}

.coord-line {
  color: #334155;
  font-weight: 500;
  font-size: 13px;
  margin: 0 0 4px;
}

.form-section-wrap {
  position: sticky;
  top: 0;
}

.info-card--map {
  display: flex;
  flex-direction: column;
}

.map-search-line {
  align-items: flex-start;
  width: 100%;
}

.map-search-line--full {
  display: flex;
}

.map-search-input {
  width: 100%;
  max-width: none;
}

.map-search-wrap {
  position: relative;
  width: 100%;
}

.coord-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  max-width: 100%;
}

.coord-line-form {
  gap: 10px;
  min-width: 0;
}

.coord-input {
  width: 100%;
  max-width: none;
}

.map-search-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 10;
  max-height: 240px;
  overflow: auto;
  border-radius: 16px;
  background: #fff;
  border: 1px solid rgba(58, 104, 255, 0.16);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
}

.map-search-dropdown__item {
  padding: 10px 14px;
  cursor: pointer;
}

.map-search-dropdown__item+.map-search-dropdown__item {
  border-top: 1px solid rgba(226, 232, 240, 0.8);
}

.map-search-dropdown__item:hover {
  background: rgba(58, 104, 255, 0.06);
}

.map-search-dropdown__title {
  color: #1e293b;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.map-search-dropdown__address {
  margin-top: 2px;
  color: rgba(30, 41, 59, 0.68);
  font-size: 12px;
  line-height: 1.45;
}

.map-canvas {
  position: relative;
  width: 100%;
  height: 320px;
  min-height: 280px;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid rgba(58, 104, 255, 0.18);
  background: #eef2f9;
  cursor: crosshair;
}

.map-canvas--readonly {
  cursor: default;
  pointer-events: none;
}

.photo-slots {
  margin-top: 4px;
}

.photo-slot {
  position: relative;
  width: 96px;
  height: 96px;
  border: 1px dashed rgba(58, 104, 255, 0.4);
  cursor: pointer;
  overflow: hidden;
  background: rgba(247, 249, 255, 0.7);
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.photo-slot:hover {
  background: rgba(58, 104, 255, 0.08);
  border-color: #3A68FF;
  box-shadow: 0 0 0 1px rgba(58, 104, 255, 0.15);
}

.photo-slot--readonly {
  cursor: default;
  pointer-events: none;
}

.photo-slot--readonly:hover {
  background: rgba(247, 249, 255, 0.7);
  border-color: rgba(58, 104, 255, 0.4);
  box-shadow: none;
}

.photo-slot:focus-visible {
  outline: 2px solid rgba(58, 104, 255, 0.45);
  outline-offset: 2px;
}

.photo-preview {
  width: 100%;
  height: 100%;
}

.photo-slot__remove {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.72);
  cursor: pointer;
  z-index: 1;
}

.photo-slot__remove:hover {
  background: rgba(15, 23, 42, 0.88);
}

.form-line {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.form-line--address {
  gap: 10px;
}

.form-line__label {
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 14px;
  line-height: 14px;
  color: rgba(0, 0, 0, 0.7);
  min-width: 96px;
  text-align: right;
}

.form-line__label--required {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}

.required-mark {
  color: #ff4d4f;
  font-size: 14px;
  line-height: 1;
}

.form-line--compact .form-line__label {
  min-width: 96px;
}

.form-line--address .form-line__label,
.form-line--address.form-line--compact .form-line__label {
  min-width: 84px;
}

.form-line__control--switch {
  display: flex;
  align-items: center;
  width: auto;
  max-width: none;
}

.step-basic :deep(.strategy-switch) {
  --el-switch-on-color: #426CF9;
}

/* 自定义控制权限：三个复选框横向排列，窄屏自动换行 */
.control-perms {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 24px;
  width: auto;
  max-width: none;
}

.step-basic :deep(.el-checkbox) {
  --el-checkbox-checked-bg-color: #426CF9;
  --el-checkbox-checked-input-border-color: #426CF9;
  --el-checkbox-checked-text-color: #2E2E2E;
  margin-right: 0;
  height: auto;
}

.step-basic :deep(.v-field--variant-outlined) {
  min-height: 40px;
  background: #fff;
  border-radius: 999px;
  color: #2E2E2E;
}

.step-basic :deep(.v-field--variant-outlined .v-field__outline) {
  --v-field-border-opacity: 1;
  color: #dfe5f2;
}

.step-basic :deep(.v-field--variant-outlined:hover .v-field__outline) {
  color: rgba(38, 85, 241, 0.35);
}

.step-basic :deep(.v-field--focused .v-field__outline) {
  --v-field-border-opacity: 1;
  color: #2655f1;
}

.step-basic :deep(.v-field__input) {
  min-height: 40px;
  padding-top: 8px;
  padding-bottom: 8px;
  font-size: 14px;
}

.step-basic :deep(.v-field__append-inner),
.step-basic :deep(.v-field__clearable) {
  align-items: center;
  align-self: center;
  padding-top: 0;
  padding-bottom: 0;
}

.step-basic :deep(.v-label.v-field-label) {
  color: rgba(0, 0, 0, 0.55);
  opacity: 1;
  font-size: 14px;
}

.step-basic :deep(.v-field--active .v-label.v-field-label) {
  color: rgba(0, 0, 0, 0.65);
}

.step-basic :deep(input::placeholder) {
  color: rgba(0, 0, 0, 0.38);
  opacity: 1;
}

.step-basic :deep(.v-checkbox .v-label) {
  color: #2E2E2E;
  opacity: 0.85;
  font-size: 13px;
}

.step-basic :deep(.v-switch .v-selection-control) {
  min-height: 28px;
}

.step-basic :deep(.v-switch .v-selection-control__wrapper) {
  margin-inline-start: 0;
  width: 40px;
  height: 24px;
}

.step-basic :deep(.v-switch .v-selection-control__input) {
  width: 40px;
  height: 24px;
}

.step-basic :deep(.v-switch .v-switch__track) {
  min-width: 40px;
  height: 24px;
}

.step-basic :deep(.v-switch .v-switch__thumb) {
  width: 18px;
  height: 18px;
}

:deep(.v-picker__header-wrapper) {
  display: none !important;
}

@media (max-width: 1366px) {
  .info-card {
    padding: 16px 18px;
    border-radius: 20px;
  }

  .map-canvas {
    height: 280px;
  }
}

@media (max-width: 720px) {
  .form-line {
    flex-direction: column;
    align-items: stretch;
    gap: 4px;
  }

  .form-line__label,
  .form-line--compact .form-line__label {
    min-width: 0;
    text-align: left;
  }

  .form-line__control--switch {
    width: auto;
  }
}

@media (prefers-reduced-motion: reduce) {
  .photo-slot {
    transition: none;
  }
}
</style>
